import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, CheckCircle2, Loader2, RotateCcw, Search, X } from 'lucide-react';
import QuestionReportService from '../../../sdk/services/QuestionReportService';
import type { ReportCategory, ReportGroup, ReportStatus } from '../../../sdk/@types/Forum';
import { readApiError } from '../../../sdk/errors/apiError';
import AccessDenied from '../AccessDenied/AccessDenied';
import ReportDetail, { type ReportOutcomeInfo } from './ReportDetail';
import {
  REPORT_CATEGORY_LABELS,
  REPORT_CATEGORY_OPTIONS,
  REPORT_STATUS_LABELS,
  questionRefLine,
  relativeTime,
  reportCountLabel,
} from './reportLabels';

const cx = (...v: Array<string | false | null | undefined>) => v.filter(Boolean).join(' ');

const FOCUS =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-select/60 ' +
  'focus-visible:ring-offset-2 focus-visible:ring-offset-surface';

/**
 * Ordem das abas, e também a ordem das quatro chamadas da montagem. Abertas
 * primeiro, porque é onde está o trabalho; os três desfechos depois.
 */
const STATUSES: ReportStatus[] = ['open', 'resolved', 'accepted', 'rejected'];

/** Cada sub-fila diz o que **não** aconteceu nela, não um "nada aqui" genérico. */
const EMPTY_COPY: Record<ReportStatus, string> = {
  open: 'Nenhuma denúncia aberta. Nada para triar agora.',
  resolved: 'Nenhuma denúncia concluída como corrigida ainda.',
  accepted: 'Nenhuma questão desativada por denúncia ainda.',
  rejected: 'Nenhuma denúncia rejeitada ainda.',
};

type Lists = Record<ReportStatus, ReportGroup[]>;

const emptyLists = (): Lists => ({ open: [], resolved: [], accepted: [], rejected: [] });

const notifiedLabel = (n: number) => (n === 1 ? '1 aluno notificado' : `${n} alunos notificados`);

/**
 * Acrescenta a contagem de notificados ao desfecho — **só quando ela existe**.
 *
 * A contagem vem da API, que a produziu ao escrever as notificações. Quando a
 * resposta não a traz, `notified` chega ausente e a frase sai sem a segunda
 * metade: o desfecho é certo, o número é que não se sabe. Interpolar um
 * `undefined` (ou o `NaN` de um `Number(undefined)`) mostraria lixo ao
 * moderador num aviso que ele lê como confirmação.
 */
function withNotified(sentence: string, notified: number | undefined): string {
  return Number.isFinite(notified) ? `${sentence} · ${notifiedLabel(notified as number)}` : sentence;
}

/**
 * A frase do aviso de cada desfecho. `already-decided` não fala de notificação
 * porque quem notificou foi a ação do outro moderador, não esta.
 */
function outcomeMessage(info: ReportOutcomeInfo): string {
  if (info.kind === 'deactivated') {
    return withNotified(`Questão #${info.questionNumber} desativada`, info.notified);
  }
  if (info.kind === 'resolved') {
    return withNotified(
      `Questão #${info.questionNumber} concluída como corrigida`,
      info.notified
    );
  }
  return `A denúncia da questão #${info.questionNumber} já tinha sido resolvida por outro moderador.`;
}

export interface ReportsQueueProps {
  /** Sub-fila inicial — o padrão é Abertas, que é onde o trabalho está. */
  initialStatus?: ReportStatus;
  /**
   * Um grupo saiu de Abertas (corrigida, rejeitada ou desativada) — o
   * chamador decrementa `openReports` no contador compartilhado. Coerente com
   * `ModerationQueue.onPendingResolved`: a fila já sabe quantos grupos saíram,
   * então subir a contagem em vez de rebuscar `/moderation/stats` evita um
   * round-trip por decisão.
   */
  onReportResolved?: (count: number) => void;
}

/**
 * Aba Denúncias de `/admin/moderacao` (frames `Pgime` / `cQlXD`).
 *
 * Quatro sub-filas agrupadas **por questão**: três alunos denunciando a mesma
 * questão são um item só, com a contagem. O agrupamento em si vive no serviço
 * (`groupReports`), que aceita tanto a lista já agrupada do contrato quanto a
 * lista crua de denúncias.
 *
 * Cada sub-fila é nomeada pelo desfecho que representa — Abertas, Corrigidas,
 * Desativadas, Rejeitadas (ver `REPORT_STATUS_LABELS`).
 *
 * As quatro são buscadas de uma vez na montagem, e não uma por aba: as ações
 * movem itens entre elas (concluir tira de Abertas e põe em Corrigidas,
 * rejeitar põe em Rejeitadas, o Desfazer faz o caminho de volta), e uma fila
 * que ainda não foi carregada não tem para onde receber o item movido.
 */
export default function ReportsQueue({
  initialStatus = 'open',
  onReportResolved,
}: ReportsQueueProps) {
  const navigate = useNavigate();

  const [status, setStatus] = useState<ReportStatus>(initialStatus);
  const [lists, setLists] = useState<Lists>(emptyLists);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ReturnType<typeof readApiError> | null>(null);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<ReportCategory | 'all'>('all');
  const [selected, setSelected] = useState<string | null>(null);
  const [toast, setToast] = useState<ReportOutcomeInfo | null>(null);
  const [undoing, setUndoing] = useState(false);

  const load = useCallback(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    Promise.all(STATUSES.map((s) => QuestionReportService.listReports({ status: s })))
      .then((results) => {
        if (!alive) return;
        // Casado por índice, e não por destructuring posicional: um quinto
        // desfecho, um dia, é um item a mais em STATUSES e nada mais.
        const next = emptyLists();
        STATUSES.forEach((s, i) => {
          next[s] = results[i];
        });
        setLists(next);
      })
      .catch((err) => {
        if (alive) {
          setError(readApiError(err));
          setLists(emptyLists());
        }
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => load(), [load]);

  /** Busca e motivo estreitam a lista da sub-fila corrente, no cliente. */
  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return lists[status].filter((group) => {
      if (category !== 'all' && group.category !== category) return false;
      if (!term) return true;
      const haystack = [
        `#${group.questionRef.number ?? ''}`,
        String(group.questionRef.number ?? ''),
        group.questionRef.agency,
        group.questionRef.year,
        group.questionRef.subject,
        group.questionRef.topic,
        REPORT_CATEGORY_LABELS[group.category],
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [lists, status, search, category]);

  useEffect(() => {
    if (!visible.length) {
      setSelected(null);
      return;
    }
    if (!visible.some((g) => g.questionId === selected)) {
      setSelected(visible[0].questionId);
    }
  }, [visible, selected]);

  const move = useCallback((questionId: string, from: ReportStatus, to: ReportStatus) => {
    setLists((prev) => {
      const item = prev[from].find((g) => g.questionId === questionId);
      if (!item) return prev;
      return {
        ...prev,
        [from]: prev[from].filter((g) => g.questionId !== questionId),
        [to]: [{ ...item, status: to }, ...prev[to].filter((g) => g.questionId !== questionId)],
      };
    });
  }, []);

  /**
   * Um desfecho chegou do detalhe: o grupo vai para a sub-fila de destino **em
   * memória**, sem refazer a busca dela — é para isso que as quatro são
   * carregadas na montagem. Refazer a busca do destino perderia a corrida com o
   * item recém-movido, que o servidor ainda pode não estar listando.
   *
   * `onResolved` só é chamado pelos botões de decisão do `ReportDetail`, que só
   * aparecem com `canDecide` (`status === 'open'`) — então toda chamada aqui
   * corresponde a um grupo que de fato estava em Abertas, e `onReportResolved`
   * sobe para a view decrementar `openReports` no contador compartilhado.
   */
  const onResolved = useCallback(
    (next: ReportStatus, info?: ReportOutcomeInfo) => {
      if (!selected) return;
      move(selected, status, next);
      onReportResolved?.(1);
      if (info) setToast(info);
    },
    [move, selected, status, onReportResolved]
  );

  /**
   * "Desfazer" do toast chama o endpoint `undo-accept`.
   *
   * Não é um atraso no cliente antes de aplicar: a desativação vale para todos
   * assim que confirmada, então voltar atrás também precisa ir ao servidor
   * (decisão 9 do design).
   */
  const undo = useCallback(async () => {
    if (!toast || undoing) return;
    try {
      setUndoing(true);
      await QuestionReportService.undoAccept(toast.questionId);
      move(toast.questionId, 'accepted', 'open');
      setToast(null);
    } catch {
      // 401/403 já viram toast pelo interceptor; o botão volta a ficar clicável.
    } finally {
      setUndoing(false);
    }
  }, [move, toast, undoing]);

  if (error?.kind) {
    return <AccessDenied kind={error.kind} detail={error.raw} variant="panel" onNavigate={navigate} />;
  }

  const failed = Boolean(error && !error.kind);

  return (
    <div className="flex min-h-0 flex-col gap-3" data-testid="reports-queue">
      {/* ── Sub-filas + busca + filtro de motivo ────────────────────────── */}
      <div className="flex flex-wrap items-center gap-2">
        <div role="tablist" aria-label="Sub-filas de denúncias" className="flex items-center gap-1">
          {STATUSES.map((s) => (
            <button
              key={s}
              type="button"
              role="tab"
              aria-selected={status === s}
              onClick={() => setStatus(s)}
              className={cx(
                'h-9 rounded-chip border px-3 text-sm transition-colors duration-150',
                'motion-reduce:transition-none',
                status === s
                  ? 'border-select/[0.42] bg-select/[0.14] font-semibold text-select-ink'
                  : 'border-edge bg-surface text-ink-secondary hover:border-edge-strong hover:bg-surface-hover',
                FOCUS
              )}
            >
              {REPORT_STATUS_LABELS[s]}
            </button>
          ))}
        </div>

        <div className="relative min-w-[220px] flex-1">
          <Search
            size={15}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted"
          />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Buscar por nº da questão, banca ou motivo"
            placeholder="Buscar por nº da questão, banca ou motivo"
            className={cx(
              'h-9 w-full rounded-input border border-edge bg-surface pl-9 pr-3 text-sm',
              'text-ink placeholder:text-ink-muted',
              FOCUS
            )}
          />
        </div>

        <select
          aria-label="Filtrar por motivo"
          value={category}
          onChange={(e) => setCategory(e.target.value as ReportCategory | 'all')}
          className={cx(
            'h-9 rounded-input border border-edge bg-surface px-2.5 text-sm text-ink-secondary',
            FOCUS
          )}
        >
          <option value="all">Motivo: Todos</option>
          {REPORT_CATEGORY_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        <button
          type="button"
          onClick={load}
          aria-label="Atualizar filas"
          className={cx(
            'inline-flex h-9 w-9 items-center justify-center rounded-input border border-edge',
            'bg-surface text-ink-secondary hover:border-edge-strong hover:bg-surface-hover',
            FOCUS
          )}
        >
          <RotateCcw size={15} />
        </button>
      </div>

      <div className="grid min-h-0 gap-4 lg:grid-cols-[320px_minmax(0,1fr)]">
        {/* ── Fila ─────────────────────────────────────────────────────── */}
        <div role="tabpanel" className="flex min-h-0 flex-col gap-2">
          {loading && (
            <div
              role="status"
              aria-label="Carregando denúncias"
              className="flex items-center justify-center rounded-card border border-edge bg-surface py-10"
            >
              <Loader2 size={18} className="animate-spin text-ink-muted" />
            </div>
          )}

          {/*
            Falha que não é negativa de permissão — hoje, tipicamente o 404 das
            rotas de moderação que ainda não existem no Go API. Precisa dizer
            "não carregou": mostrar "nenhuma denúncia aberta" faria uma fila
            quebrada parecer uma fila vazia.
          */}
          {!loading && failed && (
            <div className="rounded-card border border-edge bg-surface p-4 text-sm">
              <p className="text-ink">Não foi possível carregar as denúncias.</p>
              {error?.raw && <p className="mt-1 text-xs text-ink-muted">{error.raw}</p>}
              <button
                type="button"
                onClick={load}
                className={cx(
                  'mt-3 inline-flex h-9 items-center gap-2 rounded-input border border-edge',
                  'bg-surface px-3 font-medium text-ink-secondary hover:border-edge-strong',
                  'hover:bg-surface-hover hover:text-ink',
                  FOCUS
                )}
              >
                <RotateCcw size={15} />
                Tentar de novo
              </button>
            </div>
          )}

          {!loading && !failed && !visible.length && (
            <p className="rounded-card border border-edge bg-surface p-4 text-sm text-ink-muted">
              {lists[status].length
                ? 'Nenhuma denúncia corresponde à busca.'
                : EMPTY_COPY[status]}
            </p>
          )}

          {!loading &&
            visible.map((group) => {
              const active = group.questionId === selected;
              return (
                <button
                  key={group.questionId}
                  type="button"
                  data-testid="report-group"
                  aria-pressed={active}
                  onClick={() => setSelected(group.questionId)}
                  className={cx(
                    'flex flex-col gap-1.5 rounded-card border p-3 text-left',
                    'transition-colors duration-150 motion-reduce:transition-none',
                    active
                      ? 'border-select/[0.42] bg-select/[0.14]'
                      : 'border-edge bg-surface hover:border-edge-strong hover:bg-surface-hover',
                    FOCUS
                  )}
                >
                  <span className="flex items-baseline justify-between gap-2">
                    <span className="text-sm font-semibold text-ink">
                      Questão #{group.questionRef.number ?? group.questionId}
                    </span>
                    <span className="shrink-0 text-[11px] text-ink-muted">
                      {relativeTime(group.lastAt)}
                    </span>
                  </span>
                  <span className="truncate text-xs text-ink-muted">
                    {questionRefLine(group.questionRef) || '—'}
                  </span>
                  <span className="flex flex-wrap items-center gap-1.5">
                    <span className="rounded-chip border border-warn/40 bg-warn/[0.10] px-2 py-0.5 text-[11px] font-medium text-warn-ink">
                      {REPORT_CATEGORY_LABELS[group.category]}
                    </span>
                    <span className="rounded-chip border border-edge bg-surface-muted px-2 py-0.5 text-[11px] text-ink-secondary">
                      {reportCountLabel(group.count)}
                    </span>
                  </span>
                </button>
              );
            })}
        </div>

        {/* ── Detalhe ──────────────────────────────────────────────────── */}
        <div className="min-w-0">
          {selected ? (
            <ReportDetail
              key={selected}
              questionId={selected}
              status={status}
              onResolved={onResolved}
            />
          ) : (
            <div className="flex min-h-[320px] items-center justify-center rounded-card border border-edge bg-surface p-6 text-sm text-ink-muted">
              Selecione uma denúncia para ver a questão e as respostas dos alunos.
            </div>
          )}
        </div>
      </div>

      {/* ── Aviso do desfecho ───────────────────────────────────────────────
          Um componente para os três desfechos: os três dizem a mesma coisa —
          "o que aconteceu com esta questão" — e só o aceite tem volta. A
          superfície é neutra porque nenhum deles é erro; `danger` fica com a
          ação destrutiva em si, não com o aviso de que ela aconteceu. */}
      {toast && (
        <div
          role="status"
          aria-live="polite"
          data-testid={toast.kind === 'deactivated' ? 'deactivate-toast' : 'report-outcome-toast'}
          data-kind={toast.kind}
          className={cx(
            'fixed bottom-6 right-6 z-[1100] flex items-center gap-4 rounded-card border',
            'border-edge bg-surface px-4 py-3 shadow-panel'
          )}
        >
          {toast.kind === 'resolved' && (
            <CheckCircle2 size={16} className="shrink-0 text-ink-secondary" />
          )}
          {/* `warn`: é aviso, não falha do moderador — a regra de cor do §7. */}
          {toast.kind === 'already-decided' && (
            <AlertCircle size={16} className="shrink-0 text-warn-ink" />
          )}

          <span className="text-sm text-ink">{outcomeMessage(toast)}</span>

          {toast.kind === 'deactivated' && (
            <button
              type="button"
              onClick={undo}
              disabled={undoing}
              className={cx(
                'rounded-input px-2 py-1 text-sm font-semibold text-select-ink',
                'hover:bg-surface-hover disabled:opacity-60',
                FOCUS
              )}
            >
              Desfazer
            </button>
          )}
          <button
            type="button"
            aria-label="Fechar aviso"
            onClick={() => setToast(null)}
            className={cx('text-ink-muted hover:text-ink', FOCUS)}
          >
            <X size={15} />
          </button>
        </div>
      )}
    </div>
  );
}
